import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowUpRight, Upload, FileText, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { apiErrorMessage } from '../lib/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPT = '.pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/png,image/jpeg';

const PRODUCTS = [
  { v: 'vector_settle', l: 'Vector Settle (property)' },
  { v: 'vector_bridge', l: 'Vector Bridge (property)' },
  { v: 'bulk_pay', l: 'Bulk Pay' },
  { v: 'vector_forward', l: 'Vector Forward' },
  { v: 'global_payroll', l: 'Global Payroll' },
  { v: 'multi_currency', l: 'Multi-currency account' },
  { v: 'personal_transfers', l: 'Personal transfers' },
  { v: 'other', l: 'Other / not sure' },
];

export default function ComplaintsModal({ open, onOpenChange }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: '', email: '', subject: '', description: '',
    product: 'other', contact_method: 'email', urgency: 'medium', reference_id: '',
  });
  const [attachment, setAttachment] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error('Attachment is too large. Maximum 8 MB.');
      return;
    }
    setAttachment(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.description) {
      return toast.error('Please complete every required field.');
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (attachment) fd.append('attachment', attachment);
      await axios.post(`${API}/complaints`, fd);
      toast.success('Complaint received. Our team will respond within 3 business days.');
      setForm({ name: '', email: '', subject: '', description: '',
        product: 'other', contact_method: 'email', urgency: 'medium', reference_id: '' });
      setAttachment(null);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not submit complaint'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="complaints-modal"
        className="max-w-[560px] max-h-[90vh] overflow-y-auto bg-[#F5F1E8] p-0"
      >
        <div className="p-7 pb-2">
          <DialogTitle asChild>
            <h2 className="font-serif text-[26px] leading-tight text-[#0B3B2E]">Raise a complaint</h2>
          </DialogTitle>
          <DialogDescription className="text-[#0E1F1A]/60 mt-1 text-[13px]">
            We take every complaint seriously. Tell us what happened and we&rsquo;ll investigate within 3 business days.
          </DialogDescription>
        </div>
        <form onSubmit={submit} className="px-7 pb-7 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Full name *">
              <Input data-testid="complaint-name" value={form.name} onChange={(e) => set('name', e.target.value)} />
            </Row>
            <Row label="Email *">
              <Input data-testid="complaint-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </Row>
          </div>

          <Row label="Subject *">
            <Input data-testid="complaint-subject" value={form.subject} onChange={(e) => set('subject', e.target.value)}
              placeholder="One line — what is this about?" />
          </Row>

          <Row label="Description *">
            <textarea data-testid="complaint-description" value={form.description} onChange={(e) => set('description', e.target.value)}
              rows={4}
              placeholder="Include dates, amounts and any relevant reference numbers."
              className="w-full rounded-md border border-[#0B3B2E]/20 bg-white p-2.5 text-[14px]" />
          </Row>

          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Related product / service">
              <select data-testid="complaint-product" value={form.product} onChange={(e) => set('product', e.target.value)}
                className="w-full h-10 rounded-md border border-[#0B3B2E]/20 bg-white px-2.5 text-[14px]">
                {PRODUCTS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </Row>
            <Row label="Preferred contact method">
              <select data-testid="complaint-contact" value={form.contact_method} onChange={(e) => set('contact_method', e.target.value)}
                className="w-full h-10 rounded-md border border-[#0B3B2E]/20 bg-white px-2.5 text-[14px]">
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="either">Either is fine</option>
              </select>
            </Row>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Urgency">
              <select data-testid="complaint-urgency" value={form.urgency} onChange={(e) => set('urgency', e.target.value)}
                className="w-full h-10 rounded-md border border-[#0B3B2E]/20 bg-white px-2.5 text-[14px]">
                <option value="low">Low — informational</option>
                <option value="medium">Medium — response needed</option>
                <option value="high">High — funds affected</option>
              </select>
            </Row>
            <Row label="Order / reference ID (optional)">
              <Input data-testid="complaint-reference" value={form.reference_id} onChange={(e) => set('reference_id', e.target.value)}
                placeholder="e.g. TXN-9081 or invoice number" />
            </Row>
          </div>

          <div className="space-y-1">
            <Label className="text-[12px] text-[#0E1F1A]/65">Supporting document (optional · PDF/DOC/TXT/PNG/JPG · max 8 MB)</Label>
            {!attachment ? (
              <button type="button" onClick={() => fileRef.current?.click()}
                data-testid="complaint-attachment-btn"
                className="w-full flex items-center justify-center gap-2 h-14 rounded-md border-2 border-dashed border-[#0B3B2E]/25 bg-white/60 text-[#0B3B2E]/70 hover:bg-white hover:border-[#0B3B2E]/50 transition-colors">
                <Upload className="w-4 h-4" />
                <span className="text-[13px] font-medium">Attach a file</span>
              </button>
            ) : (
              <div className="flex items-center gap-3 rounded-md border border-[#0B3B2E]/20 bg-white p-3">
                <FileText className="w-5 h-5 text-[#0B3B2E]" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-[#0B3B2E] truncate">{attachment.name}</p>
                  <p className="text-[11px] text-[#0E1F1A]/55">{(attachment.size / 1024).toFixed(0)} KB</p>
                </div>
                <button type="button" onClick={() => setAttachment(null)} className="text-[#0E1F1A]/50 hover:text-rose-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <input ref={fileRef} type="file" accept={ACCEPT} onChange={pickFile} className="hidden" />
          </div>

          <Button type="submit" disabled={busy}
            data-testid="complaint-submit"
            className="w-full rounded-full h-11 bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] font-medium disabled:opacity-70">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending</>
              : <>Submit complaint <ArrowUpRight className="w-4 h-4 ml-1" /></>}
          </Button>
          <p className="text-center text-[11px] text-[#0E1F1A]/55">
            We log every complaint securely. A written response follows within 3 business days per FCA guidance.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[12px] text-[#0E1F1A]/65">{label}</Label>
      {children}
    </div>
  );
}
